import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { ROLE_CONFIG, ROLE_DESCRIPTIONS, PERMISSIONS_MATRIX, TEAM_MEMBERS } from './teamData';

const ROLE_MEMBER_COUNTS = {
  Admin: TEAM_MEMBERS?.filter(m => m?.role === 'Admin')?.length,
  Investor: TEAM_MEMBERS?.filter(m => m?.role === 'Investor')?.length,
  Sponsor: TEAM_MEMBERS?.filter(m => m?.role === 'Sponsor')?.length,
  Analyst: TEAM_MEMBERS?.filter(m => m?.role === 'Analyst')?.length,
  Viewer: TEAM_MEMBERS?.filter(m => m?.role === 'Viewer')?.length,
};

function PermCell({ value }) {
  if (value === 'full') return <span className="flex items-center justify-center"><span className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center"><Icon name="Check" size={13} color="#16a34a" /></span></span>;
  if (value === 'none') return <span className="flex items-center justify-center"><span className="w-6 h-6 rounded-full bg-red-50 flex items-center justify-center"><Icon name="X" size={13} color="#dc2626" /></span></span>;
  return <span className="flex items-center justify-center"><span className="w-6 h-6 rounded-full bg-amber-50 flex items-center justify-center text-amber-600 font-bold text-xs">~</span></span>;
}

export default function RolesPermissionsTab({ isAdmin, showToast }) {
  const [expandedRole, setExpandedRole] = useState(null);

  return (
    <div className="space-y-8">
      {/* Role Cards */}
      <div>
        <h3 className="font-heading text-base font-bold text-foreground mb-4">Roles Overview</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {Object.entries(ROLE_CONFIG)?.map(([role, cfg]) => (
            <div key={role} className="bg-card rounded-2xl border border-border p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full font-caption text-xs font-bold ${cfg?.color}`}>{role}</span>
                <span className="font-caption text-xs text-muted-foreground font-medium">{ROLE_MEMBER_COUNTS?.[role]} member{ROLE_MEMBER_COUNTS?.[role] !== 1 ? 's' : ''}</span>
              </div>
              <p className="font-caption text-xs text-muted-foreground leading-relaxed flex-1">{ROLE_DESCRIPTIONS?.[role]}</p>
              {isAdmin && (
                <button
                  onClick={() => showToast('Custom role permissions saved')}
                  className="w-full px-3 py-1.5 rounded-lg font-caption text-xs font-semibold border border-border text-muted-foreground hover:bg-muted transition-colors"
                >
                  Edit Permissions
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Permissions Matrix */}
      <div>
        <h3 className="font-heading text-base font-bold text-foreground mb-4">Permissions Matrix</h3>
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="bg-muted border-b border-border">
                  <th className="px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide w-48">Permission</th>
                  {Object.entries(ROLE_CONFIG)?.map(([role, cfg]) => (
                    <th key={role} className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-caption text-xs font-bold ${cfg?.color}`}>{role}</span>
                        {isAdmin && (
                          <button
                            onClick={() => showToast('Custom role permissions saved')}
                            className="font-caption text-xs text-primary hover:text-primary/80 font-medium"
                          >
                            Customise
                          </button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/50">
                {PERMISSIONS_MATRIX?.map((row, i) => (
                  <tr key={row?.permission} className={i % 2 === 0 ? 'bg-card' : 'bg-muted/50'}>
                    <td className="px-4 py-3 font-caption text-sm text-foreground font-medium">{row?.permission}</td>
                    {['Admin', 'Investor', 'Sponsor', 'Analyst', 'Viewer']?.map(role => (
                      <td key={role} className="px-4 py-3 text-center"><PermCell value={row?.[role]} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Legend */}
          <div className="flex items-center gap-6 px-4 py-3 border-t border-border bg-muted">
            <span className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">Legend:</span>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center"><Icon name="Check" size={11} color="#16a34a" /></span>
              <span className="font-caption text-xs text-foreground">Full access</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-red-50 flex items-center justify-center"><Icon name="X" size={11} color="#dc2626" /></span>
              <span className="font-caption text-xs text-foreground">No access</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-amber-50 flex items-center justify-center text-amber-600 font-bold text-xs">~</span>
              <span className="font-caption text-xs text-foreground">Limited access</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
